import { WORKY_WEB_APP_PATHS } from '../../WorkyWebAppPaths';

interface HotIntakeSelectOption {
  readonly value: string;
  readonly label: string;
}

const HOT_INTAKE_NEW_AGENCY_INCENTIVE_COPY: string =
  'New agencies can qualify for up to $1,000 of incentives on Worky.';
const HOT_INTAKE_NEW_AGENCY_INCENTIVE_BANNER_COPY: string =
  'New agencies can qualify for up to $1,000 in incentives';
const HOT_INTAKE_NEW_AGENCY_INCENTIVE_BANNER_LEARN_MORE_LABEL: string = 'Learn more';

/** Client-defined employee count range options for hot-intake forms. */
const HOT_INTAKE_ACTIVE_EMPLOYEES_OPTIONS: readonly HotIntakeSelectOption[] = [
  { value: '1-20', label: '1-20' },
  { value: '21-75', label: '21-75' },
  { value: '76+', label: '76+' },
];

/** Wire values must match server HotIntakePrimaryServiceArea. */
const HOT_INTAKE_PRIMARY_SERVICE_AREA_OPTIONS: readonly HotIntakeSelectOption[] = [
  { value: 'Home care', label: 'Home care' },
  { value: 'General staffing', label: 'General staffing' },
  { value: 'Non-medical support', label: 'Non-medical support' },
  { value: 'Healthcare support', label: 'Healthcare support' },
  { value: 'Medical support', label: 'Medical support' },
  { value: 'Other', label: 'Other' },
];

/** Wire values must match server HotIntakeOperatingTenure. */
const HOT_INTAKE_OPERATING_TENURE_OPTIONS: readonly HotIntakeSelectOption[] = [
  { value: '<1 year', label: '<1 year' },
  { value: '1-2 years', label: '1-2 years' },
  { value: '3-5 years', label: '3-5 years' },
  { value: '6+ years', label: '6+ years' },
];

const HOT_INTAKE_EMAIL_PATTERN: RegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Keep in sync with server HotIntakeLeadSpamClassifier blocked domains. */
const BLOCKED_HOT_INTAKE_EMAIL_DOMAINS: readonly string[] = [
  'airhemp.com',
  'mailinator.com',
  'guerrillamail.com',
  'guerrillamailblock.com',
  'sharklasers.com',
  'grr.la',
  'pokemail.net',
  'spam4.me',
  'yopmail.com',
  '10minutemail.com',
  'tempmail.com',
  'trashmail.com',
];

const HOT_INTAKE_BLOCKED_EMAIL_SNACKBAR: string =
  'Please use a work email. Temporary or disposable addresses are not accepted.';

const resolveHotIntakeEmailDomain = (contactChannelValue: string): string => {
  const trimmedContactChannelValue: string = contactChannelValue.trim().toLowerCase();
  const atIndex: number = trimmedContactChannelValue.lastIndexOf('@');
  if (atIndex < 0 || atIndex === trimmedContactChannelValue.length - 1) {
    return '';
  }
  return trimmedContactChannelValue.slice(atIndex + 1);
};

const isBlockedHotIntakeEmailDomain = (contactChannelValue: string): boolean => {
  const prospectEmailDomain: string = resolveHotIntakeEmailDomain(contactChannelValue);
  if (prospectEmailDomain.length === 0) {
    return false;
  }
  return BLOCKED_HOT_INTAKE_EMAIL_DOMAINS.some((blockedHotIntakeEmailDomain: string): boolean => {
    return (
      prospectEmailDomain === blockedHotIntakeEmailDomain
      || prospectEmailDomain.endsWith(`.${blockedHotIntakeEmailDomain}`)
    );
  });
};

const isValidHotIntakeEmail = (contactChannelValue: string): boolean => {
  const trimmedContactChannelValue: string = contactChannelValue.trim();
  if (!HOT_INTAKE_EMAIL_PATTERN.test(trimmedContactChannelValue)) {
    return false;
  }
  return !isBlockedHotIntakeEmailDomain(trimmedContactChannelValue);
};

const isValidHotIntakePhone = (contactChannelValue: string): boolean => {
  const digitsOnly: string = contactChannelValue.replace(/\D/g, '');
  return digitsOnly.length >= 10 && digitsOnly.length <= 15;
};

/**
 * Client-side check: valid email or phone (at least one format).
 * Server remains the source of truth.
 */
const isValidHotIntakeContactChannel = (contactChannelValue: string): boolean => {
  const trimmedContactChannelValue: string = contactChannelValue.trim();
  if (trimmedContactChannelValue.length === 0) {
    return false;
  }
  return (
    isValidHotIntakeEmail(trimmedContactChannelValue) ||
    isValidHotIntakePhone(trimmedContactChannelValue)
  );
};

const isValidOptionalHotIntakePhone = (contactPhoneNumber: string): boolean => {
  const trimmedContactPhoneNumber: string = contactPhoneNumber.trim();
  if (trimmedContactPhoneNumber.length === 0) {
    return true;
  }
  return isValidHotIntakePhone(trimmedContactPhoneNumber);
};

const HOT_INTAKE_PREFERRED_CALL_DATE_PATTERN: RegExp = /^\d{4}-\d{2}-\d{2}$/;
const HOT_INTAKE_PREFERRED_CALL_TIME_TYPED_PATTERN: RegExp =
  /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i;
const HOT_INTAKE_PREFERRED_CALL_TIME_PLACEHOLDER: string = 'e.g. 11:00 AM';

const isValidHotIntakePreferredCallDate = (preferredCallDate: string): boolean => {
  const trimmedPreferredCallDate: string = preferredCallDate.trim();
  if (!HOT_INTAKE_PREFERRED_CALL_DATE_PATTERN.test(trimmedPreferredCallDate)) {
    return false;
  }
  const preferredCallDateParts: readonly string[] = trimmedPreferredCallDate.split('-');
  const preferredCallYear: number = Number(preferredCallDateParts[0]);
  const preferredCallMonth: number = Number(preferredCallDateParts[1]);
  const preferredCallDay: number = Number(preferredCallDateParts[2]);
  const parsedPreferredCallDate: Date = new Date(preferredCallYear, preferredCallMonth - 1, preferredCallDay);
  return (
    parsedPreferredCallDate.getFullYear() === preferredCallYear &&
    parsedPreferredCallDate.getMonth() === preferredCallMonth - 1 &&
    parsedPreferredCallDate.getDate() === preferredCallDay
  );
};

/**
 * Parses a typed preferred-call time (12-hour or 24-hour) into HH:mm.
 * Returns an empty string when the value is not a valid time.
 */
const normalizeHotIntakePreferredCallTime = (preferredCallTime: string): string => {
  const trimmedPreferredCallTime: string = preferredCallTime.trim().replace(/\s+/g, ' ').toLowerCase();
  const preferredCallTimeMatch: RegExpMatchArray | null = trimmedPreferredCallTime.match(
    HOT_INTAKE_PREFERRED_CALL_TIME_TYPED_PATTERN
  );
  if (preferredCallTimeMatch === null) {
    return '';
  }
  const typedHour: number = Number(preferredCallTimeMatch[1]);
  const typedMinute: number =
    preferredCallTimeMatch[2] === undefined ? 0 : Number(preferredCallTimeMatch[2]);
  const meridiem: string | undefined = preferredCallTimeMatch[3];
  if (Number.isNaN(typedHour) || Number.isNaN(typedMinute) || typedMinute > 59) {
    return '';
  }
  if (meridiem !== undefined) {
    if (typedHour < 1 || typedHour > 12) {
      return '';
    }
    const hour24: number =
      typedHour === 12 ? (meridiem === 'am' ? 0 : 12) : meridiem === 'am' ? typedHour : typedHour + 12;
    return `${String(hour24).padStart(2, '0')}:${String(typedMinute).padStart(2, '0')}`;
  }
  if (typedHour > 23) {
    return '';
  }
  return `${String(typedHour).padStart(2, '0')}:${String(typedMinute).padStart(2, '0')}`;
};

const isValidHotIntakePreferredCallTime = (preferredCallTime: string): boolean => {
  return normalizeHotIntakePreferredCallTime(preferredCallTime).length > 0;
};

/**
 * Date and time are optional. If a field is filled, that field must be a valid yyyy-MM-dd or time.
 */
const isValidOptionalHotIntakePreferredCallSlot = (
  preferredCallDate: string,
  preferredCallTime: string
): boolean => {
  const trimmedPreferredCallDate: string = preferredCallDate.trim();
  const trimmedPreferredCallTime: string = preferredCallTime.trim();
  if (trimmedPreferredCallDate.length > 0 && !isValidHotIntakePreferredCallDate(trimmedPreferredCallDate)) {
    return false;
  }
  if (trimmedPreferredCallTime.length > 0 && !isValidHotIntakePreferredCallTime(trimmedPreferredCallTime)) {
    return false;
  }
  return true;
};

const toggleHotIntakePrimaryServiceArea = (
  selectedServiceAreas: readonly string[],
  serviceAreaValue: string
): readonly string[] => {
  if (selectedServiceAreas.includes(serviceAreaValue)) {
    return selectedServiceAreas.filter(
      (selectedServiceArea: string): boolean => selectedServiceArea !== serviceAreaValue
    );
  }
  return [...selectedServiceAreas, serviceAreaValue];
};

/** Marker for SEO / ads first-click email CTA hot-intake submissions. */
const HOT_INTAKE_HOW_CAN_WE_HELP_FIRST_CLICK: string = 'FIRST_CLICK';

const navigateToContactSalesPage = (): void => {
  window.location.assign(WORKY_WEB_APP_PATHS.contactSales);
};

const navigateToContactSalesPageWithEmail = (email: string): void => {
  const trimmedEmail: string = email.trim();
  if (trimmedEmail.length === 0) {
    navigateToContactSalesPage();
    return;
  }
  window.location.assign(
    `${WORKY_WEB_APP_PATHS.contactSales}?email=${encodeURIComponent(trimmedEmail)}`
  );
};

export {
  HotIntakeSelectOption,
  HOT_INTAKE_ACTIVE_EMPLOYEES_OPTIONS,
  HOT_INTAKE_PRIMARY_SERVICE_AREA_OPTIONS,
  HOT_INTAKE_OPERATING_TENURE_OPTIONS,
  HOT_INTAKE_NEW_AGENCY_INCENTIVE_COPY,
  HOT_INTAKE_NEW_AGENCY_INCENTIVE_BANNER_COPY,
  HOT_INTAKE_NEW_AGENCY_INCENTIVE_BANNER_LEARN_MORE_LABEL,
  HOT_INTAKE_PREFERRED_CALL_TIME_PLACEHOLDER,
  HOT_INTAKE_BLOCKED_EMAIL_SNACKBAR,
  HOT_INTAKE_HOW_CAN_WE_HELP_FIRST_CLICK,
  isBlockedHotIntakeEmailDomain,
  isValidHotIntakeContactChannel,
  isValidHotIntakeEmail,
  isValidHotIntakePhone,
  isValidOptionalHotIntakePhone,
  isValidOptionalHotIntakePreferredCallSlot,
  navigateToContactSalesPage,
  navigateToContactSalesPageWithEmail,
  normalizeHotIntakePreferredCallTime,
  toggleHotIntakePrimaryServiceArea,
};
